import Fetch from 'utils/fetch';
import dayjs, { Dayjs } from 'dayjs';
import { Region } from 'types/orderTypes';
import { settings } from 'settings';
import { queryStringFromObject } from 'utils/urlUtil';
import { toIsoDate } from 'utils/date';
import { ShiftStatusType } from 'const/ShiftConst';
import { ERROR } from '../../const/error';
import { getUserSelectedRegion } from '../../lib/AuthClient/userPreferences';

const DATE_FORMAT = 'YYYY-MM-DD';

export interface IShift {
  id: string;
  date: string;
  region: Region;
  status: ShiftStatusType;
  courierId?: number;
  note?: string;
  extraWork: ExtraWork[];
  published: boolean;
}

export interface ExtraWork {
  typeOfExtraWork: string;
  payment: number;
}

export interface ShiftExtraWork {
  shiftId: string;
  extraWork: ExtraWork[];
}
export const createMultipleShifts = async (
  date: Dayjs,
  courierIds: number[]
): Promise<void> => {
  const response = await Fetch.post({
    url: `${settings.newApiUrl}/shift/shifts/create/multiple`,
    body: {
      region: getUserSelectedRegion(),
      date: date.format(DATE_FORMAT),
      courierIds,
    },
  });
  if (!response.ok) {
    console.error(await response.text());
    throw Error(ERROR.SHIFT_CREATE);
  }
};

export const duplicateCourierShifts = async (
  datesCouriers: Record<string, number[]>
): Promise<void> => {
  try {
    await Promise.all(
      Object.keys(datesCouriers).map(date =>
        createMultipleShifts(dayjs(date), datesCouriers[date])
      )
    );
  } catch (err) {
    throw Error(ERROR.SHIFT_COPY);
  }
};

export const createCourierShift = async (
  date: Dayjs,
  courierId: number
): Promise<void> => {
  const response = await Fetch.post({
    url: `${settings.newApiUrl}/shift/shifts/create/courier`,
    body: {
      region: getUserSelectedRegion(),
      date: date.format(DATE_FORMAT),
      courierId,
    },
  });
  if (!response.ok) {
    console.error(await response.text());
    throw Error(ERROR.SHIFT_CREATE);
  }
};

export const createOpenShift = async (date: Dayjs): Promise<void> => {
  const response = await Fetch.post({
    url: `${settings.newApiUrl}/shift/shifts/create/open`,
    body: {
      region: getUserSelectedRegion(),
      date: date.format(DATE_FORMAT),
    },
  });
  if (!response.ok) {
    console.error(await response.text());
    throw Error(ERROR.SHIFT_CREATE_OPEN_SPOT);
  }
};

export const fetchShifts = async (
  region: Region,
  start?: Dayjs,
  end?: Dayjs
): Promise<IShift[]> => {
  const startDate = start ? toIsoDate(start) : '';
  const endDate = end ? toIsoDate(end) : '';

  const queryParams = queryStringFromObject({
    start: startDate,
    end: endDate,
    region,
  });

  const response = await Fetch.get({
    url: `${settings.newApiUrl}/shift/shifts${queryParams}`,
  });

  if (!response.ok) {
    console.error(await response.text());
    throw Error(ERROR.SHIFT_FETCH);
  }
  return response.json();
};

export const removeShifts = async (shiftIds: string[]): Promise<void> => {
  const response = await Fetch.delete({
    url: `${settings.newApiUrl}/shift/shifts`,
    body: shiftIds,
  });

  if (!response.ok) {
    console.error(await response.text());
    throw Error(ERROR.SHIFT_DELETE);
  }
};

export const updateShiftStatus = async (
  id: string,
  newStatus: ShiftStatusType
): Promise<void> => {
  const response = await Fetch.put({
    url: `${settings.newApiUrl}/shift/shifts/${id}/status`,
    body: { status: newStatus },
  });

  if (!response.ok) {
    console.error(await response.text());
    throw Error(ERROR.SHIFT_STATUS_UPDATE);
  }
};

export const updateShiftNote = async (
  id: string,
  newNote: string
): Promise<void> => {
  const response = await Fetch.put({
    url: `${settings.newApiUrl}/shift/shifts/${id}/note`,
    body: { note: newNote },
  });

  if (!response.ok) {
    console.error(await response.text());
    throw Error(ERROR.SHIFT_NOTE_UPDATE);
  }
};

export const requestCourier = async (
  id: string,
  courierId: number
): Promise<void> => {
  const response = await Fetch.put({
    url: `${settings.newApiUrl}/shift/shifts/${id}/courier`,
    body: { courierId },
  });

  if (!response.ok) {
    console.error(await response.text());
    throw Error(ERROR.SHIFT_CHANGE);
  }
};

export const updateExtraWork = async (
  shiftsToUpdate: ShiftExtraWork[]
): Promise<void> => {
  const response = await Fetch.put({
    url: `${settings.newApiUrl}/shift/shifts/extra-work`,
    body: { shiftsToUpdate },
  });

  if (!response.ok) {
    console.error(await response.text());
    throw Error(ERROR.SHIFT_CHANGE);
  }
};

export const markShiftsPublished = async (
  publishedShiftIds: string[]
): Promise<void> => {
  const response = await Fetch.put({
    url: `${settings.newApiUrl}/shift/shifts/published`,
    body: publishedShiftIds,
  });

  if (!response.ok) {
    console.error(await response.text());
    throw Error(ERROR.SHIFT_PUBLISH);
  }
};

// @returns: list of courier ids mapped to dates
export const fetchCourierRequests = async (
  start: Dayjs,
  end: Dayjs,
  region: string
): Promise<{ [date: string]: number[] }> => {
  const queryParams = queryStringFromObject({
    start: toIsoDate(start),
    end: toIsoDate(end),
    region: region,
  });
  const response = await Fetch.get({
    url: `${settings.newApiUrl}/shift/shifts/courier/request${queryParams}`,
  });

  if (!response.ok) {
    console.error(await response.text());
    throw Error(ERROR.SHIFT_FETCH_WISH);
  }
  return response.json();
};

export const fetchCouriersEfficiency = async (): Promise<
  Record<string, number>
> => {
  const response = await Fetch.get({
    url: `${settings.newApiUrl}/courier/efficiency-rating`,
  });

  if (!response.ok) {
    console.error(await response.text());
    return {};
  }
  return (await response.json()) as Record<string, number>;
};

export const fetchDaysUntilCalculation = async (): Promise<number> => {
  const minDaysUntilCalc = 2;
  try {
    const response: Response = await Fetch.get({
      url: `${settings.invoiceApiUrl}/report/delay-days`,
    });

    return response.ok ? response.json() : minDaysUntilCalc;
  } catch (err) {
    console.error(
      `Failed to fetch delayDays from invoicing due to error, using default=${minDaysUntilCalc}`
    );
    return minDaysUntilCalc;
  }
};
